import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { AppBar } from '../components/AppBar';
import { ActionBar } from '../components/ActionBar';
import { ShipmentCard } from '../components/ShipmentCard';
import { Chips, CodeBox, TransferBanner, UntilBox, Verdict } from '../components/ScreenBlocks';
import { QueueStatus } from '../components/QueueStatus';
import { ReportSheet } from '../sheets/ReportSheet';
import { ConfirmTransferSheet } from '../sheets/ConfirmTransferSheet';
import { FindManuallySheet } from '../sheets/FindManuallySheet';
import { WeightSheet } from '../sheets/WeightSheet';
import { resolveNotFound, resolveScreen } from '../domain/resolveScreen';
import type { PackageInfo } from '../domain/types';
import { playFeedback } from '../feedback/sound';
import { queue, type TransferConfirmPayload } from '../api/queueRuntime';
import { FEATURES } from '../api/features';
import { useT } from '../i18n/I18nProvider';
import type { TKey } from '../i18n/dictionary';

type OpenSheet = 'none' | 'report' | 'transfer' | 'find' | 'weight';

interface Props {
  pkg: PackageInfo | null;
  scannedCode: string;
  onScanNext: () => void;
  onFindByOrder: (orderNumber: string) => Promise<void>;
  onLogout: () => void;
}

export function PackageScreen({ pkg, scannedCode, onScanNext, onFindByOrder, onLogout }: Props) {
  const { t } = useT();
  const [transferConfirmedLocally, setTransferConfirmed] = useState(false);
  const [reported, setReported] = useState(false);
  const [sheet, setSheet] = useState<OpenSheet>('none');
  const [weightOverride, setWeightOverride] = useState<number | null>(null);

  // Nová zásilka = čistý stav. Bez tohohle by po naskenování dalšího balíku
  // zůstalo svítit „Nahlášeno dispečerům" z toho předchozího.
  useEffect(() => {
    setTransferConfirmed(false);
    setReported(false);
    setWeightOverride(null);
    setSheet('none');
  }, [scannedCode]);

  // Po opravě váhy se hodnota v kartě přepíše hned, ať skladník vidí výsledek
  // a nemusí zásilku skenovat znovu.
  const shown = useMemo(
    () => (pkg && weightOverride != null ? { ...pkg, weightKg: weightOverride } : pkg),
    [pkg, weightOverride],
  );

  const model = useMemo(
    () => (shown ? resolveScreen(shown, { transferConfirmedLocally }) : resolveNotFound()),
    [shown, transferConfirmedLocally],
  );

  // Zvuk a vibrace hned po vykreslení verdiktu – telefon bývá u pasu.
  useEffect(() => {
    playFeedback(model.sound);
  }, [model.sound, scannedCode]);

  function confirmTransfer() {
    setSheet('none');
    setTransferConfirmed(true);
    if (!shown?.transfer || !FEATURES.transferConfirm) return;

    // Přes frontu: UI reaguje hned, odeslání doběhne i při slabém signálu.
    // Idempotenci drží client-side ID položky.
    const payload: TransferConfirmPayload = {
      shipmentId: shown.shipmentId ?? '',
      scannedCode,
      direction: shown.transfer.direction,
      confirmedAt: new Date().toISOString(),
    };
    void queue.enqueue('transferConfirm', payload);
  }

  const stateName = t(`s.${model.stateId}.name` as TKey);

  const styleVars = {
    '--page': model.pageBg,
    '--bar': model.barBg,
    '--bar-line': model.tokens.barLine,
    '--bar-ink': model.tokens.barInk,
    '--bar-muted': model.tokens.barMuted,
    ...(model.tier === 'stop'
      ? {
          '--ghost-ink': '#fff',
          '--ghost-line': 'rgba(255,255,255,.55)',
          '--seg-line': 'rgba(255,255,255,.5)',
          '--seg-on': '#fff',
          '--seg-on-ink': '#912018',
        }
      : {}),
  } as CSSProperties;

  return (
    <div className="app" style={styleVars}>
      <AppBar onLogout={onLogout} />

      <div className="body">
        <QueueStatus />

        <TransferBanner model={model} />

        {/* U převozu jde okno s expedicí NAD verdikt – dominantní fakt je
            „jde do druhé země“, stav je až druhotný. */}
        {model.untilBox?.aboveVerdict && <UntilBox model={model} />}

        <Verdict model={model} />

        {model.untilBox && !model.untilBox.aboveVerdict && <UntilBox model={model} />}

        <Chips model={model} />

        {model.showCodeBox && <CodeBox code={scannedCode} />}
        {model.showCard && shown && (
          <ShipmentCard model={model} pkg={shown} onEditWeight={() => setSheet('weight')} />
        )}
      </div>

      <ActionBar
        model={model}
        reported={reported}
        transferConfirmed={transferConfirmedLocally}
        transferDirection={shown?.transfer?.direction ?? null}
        onScanNext={onScanNext}
        onReport={() => setSheet('report')}
        onFindManually={() => setSheet('find')}
        onConfirmTransfer={() => setSheet('transfer')}
      />

      {sheet === 'report' && (
        <ReportSheet
          pkg={shown}
          scannedCode={scannedCode}
          stateId={model.stateId}
          onClose={() => setSheet('none')}
          onSent={() => {
            setSheet('none');
            setReported(true);
          }}
        />
      )}

      {sheet === 'transfer' && shown?.transfer && (
        <ConfirmTransferSheet
          model={model}
          direction={shown.transfer.direction}
          shipmentNumber={shown.shipmentNumber ?? scannedCode}
          stateName={stateName}
          onClose={() => setSheet('none')}
          onConfirm={confirmTransfer}
        />
      )}

      {sheet === 'find' && (
        <FindManuallySheet
          scannedCode={scannedCode}
          onClose={() => setSheet('none')}
          onSearch={async (orderNumber) => {
            await onFindByOrder(orderNumber);
            setSheet('none');
          }}
        />
      )}

      {sheet === 'weight' && shown && (
        <WeightSheet
          code={scannedCode}
          shipmentNumber={shown.shipmentNumber ?? scannedCode}
          onClose={() => setSheet('none')}
          onSaved={(weightKg) => {
            setSheet('none');
            setWeightOverride(weightKg);
          }}
        />
      )}
    </div>
  );
}
